// @flow

// !!! IMPORTANT !!!
// !!! IF YOU CHANGE THIS FILE YOU NEED TO TEST BOTH EDITORS, CLASSIC AND BETA !!!
//
// This file affects the global flows redux state, used in lots of places.
// Edit with caution and test changes thoroughly!
import _ from 'lodash';
import monotonicTimestamp from 'monotonic-timestamp';
import {
  $apply,
  $each,
  $eachPair,
  $end,
  $merge,
  $none,
  $set,
  $slice,
  has,
  update,
} from 'qim';

import ActionTypes from 'app/editor/ActionTypes';
import ApolloActionTypes from 'app/common/apollo/ActionTypes';
import ErrorActionTypes from 'app/error/ActionTypes';
import FieldUtils from 'app/beta-editor/utils/FieldUtils';
import NodeUtils, { isFilterAPI } from 'app/beta-editor/utils/NodeUtils';
import { TRASH_FOLDER_ID } from 'app/dashboard/utils/constants';
import { createReducer } from 'app/common/utils/ReduxUtils';
import { linkStateSync } from 'app/beta-editor/linkStateSync';
import {
  GUIDED_EDIT_MODE,
  NORMAL_EDIT_MODE,
} from 'app/beta-editor/constants/EditModes';
import { cleanObjectOfEmptyValues, get } from 'app/common/CommonUtils';
import {
  clearNeeds,
  updateAllNeeds,
  updateNeeds,
} from 'app/editor/reducer-utils';
import {
  createFlow,
  removeNodeFromFlow,
} from 'app/beta-editor/utils/FlowUtils';
import { getCopiedZap, isTimeoutOnSample } from 'app/beta-editor/Selectors';
import {
  getDescendants,
  getNodeAndDescendants,
} from 'app/beta-editor/utils/NodeTrees';
import {
  getRollbackSample,
  injectError,
  wasNodeRan,
} from 'app/beta-editor/utils/TaskStateUtils';
import { isPathNode, isPathsGroupNode } from 'app/beta-editor/utils/PathUtils';

import type {
  Flow,
  FlowNode,
  FlowState,
  FlowTaskState,
  MaybeSkinnyFlow,
  NodeMap,
  NodeMeta,
  NodePlanLimits,
} from 'app/beta-editor/flowState';
import type { ID } from 'app/common/types';

const LRU_MAX = 10;

type Handler = (state: FlowState, results: {}) => FlowState;
type HandlersMap = { [string]: Handler };
type TaskState = { state: FlowTaskState, next_states: FlowTaskState[] };

const initialState: FlowState = {
  // If a flow is loaded, it becomes "open". When returning to the dashboard,
  // open flows are exited.
  openFlowIds: [],
  isLoaded: false,
  didScrollErrors: true,
  flows: {},
  incidents: [],
  nextCid: 1,
  /*
    Store an LRU so we can evict older flows so as not to go crazy with memory usage.
    The LRU is just an array. It would be a premature optimization to use a linked-list, because:
    - The whole flows map gets copied every time anyway, so there's no way to make it constant time.
    - We're only going to hold a handful of items in the LRU, and it would take 1000s before we see
      any difference.
    - Without any perf tests, how do you know the VM won't optimize for you anyway?
  */
  lru: [],
  /* Per-flow node IDs which currently need to be scrolled to */
  scrollToNodeIds: {},
};

// After loading a zap, check to see if it's different than the currently loaded flow.
const hasChangedZap = (zap: Flow, flow: Flow): boolean => {
  const { ...zapProps } = zap;
  const hasMatchingProps = Object.keys(zapProps).every(key =>
    _.isEqual(zapProps[key], flow[key])
  );
  if (!hasMatchingProps) {
    return true;
  }
  const zapNodeIds = Object.keys(zap.nodes);
  const flowNodeIds = Object.keys(flow.nodes);
  if (!_.isEqual(zapNodeIds, flowNodeIds)) {
    return true;
  }

  const hasMatchingNodes = zapNodeIds.every(nodeId => {
    const zapNode = zap.nodes[nodeId];
    const flowNode = flow.nodes[nodeId];
    const hasMatchingNode = Object.keys(zapNode).every(
      key => zapNode[key] === flowNode[key]
    );
    return hasMatchingNode;
  });

  if (!hasMatchingNodes) {
    return true;
  }

  return false;
};

const isFlowInState = (state: FlowState, flowId: ID): boolean => {
  return state.flows[flowId] ? true : false;
};

const isNodeInState = (state: FlowState, flowId: ID, nodeId: ID) => {
  return (
    isFlowInState(state, flowId) &&
    state.flows[flowId].nodes &&
    state.flows[flowId].nodes[nodeId]
  );
};

const SKINNY_FLOW_PROPS = { nodes: {}, nodeIds: [], isLoaded: false };

const flowById = (state: FlowState, flowId): MaybeSkinnyFlow => {
  return (
    state.flows[flowId] || { ...SKINNY_FLOW_PROPS, id: flowId, errors: [] }
  );
};

const withFlowInState = (
  state: FlowState,
  flowId,
  handle: (flow: MaybeSkinnyFlow) => FlowState
): FlowState => {
  if (isFlowInState(state, flowId)) {
    const flow = flowById(state, flowId);
    return handle(flow);
  }
  return state;
};

const prepareNode = (node: FlowNode): FlowNode => {
  return _.extend({}, node, { meta: NodeUtils.normalizedMeta(node) });
};

const prepareNodes = (nodes: NodeMap): NodeMap => {
  nodes = _.extend({}, nodes);

  Object.keys(nodes).forEach(key => {
    nodes[key] = prepareNode(nodes[key]);
  });

  return nodes;
};

const prepareFields = fields => {
  // Zapier fields are passed in...
  fields = FieldUtils.normalizeFieldsForFormatic(fields);
  // ...and now we have Formatic fields.

  return fields;
};

const prepareNeeds = (needs = {}) => {
  return _.fromPairs(
    Object.keys(needs).map(nodeId => [
      nodeId,
      FieldUtils.removeMissingFields(
        // TODO - second arg unused?
        // prepareFields(needs[nodeId], nodes[nodeId])
        prepareFields(needs[nodeId])
      ),
    ])
  );
};

const prepareExtraNeeds = (needs = {}, nodes = {}) => {
  return _.fromPairs(
    Object.keys(needs).map(nodeId => [
      nodeId,
      FieldUtils.buildExtraFields(needs[nodeId], nodes[nodeId].params),
    ])
  );
};

const prepareMissingNeeds = (needs = {}, nodes = {}) => {
  return _.fromPairs(
    Object.keys(needs).map(nodeId => [
      nodeId,
      FieldUtils.retrieveMissingFields(needs[nodeId], nodes[nodeId].params),
    ])
  );
};

const prepareGives = gives => {
  return FieldUtils.transformGives(gives);
};

const prepareTaskState = (taskStates: ?TaskState) => {
  if (!taskStates) {
    return {};
  }

  const newTaskState = taskStates.state;
  if (!newTaskState) {
    return {};
  }

  return {
    ...newTaskState,
    input: newTaskState.input || {},
    output: newTaskState.output || {},
  };
};

const prepareFlow = flow => {
  const missingNeeds = prepareMissingNeeds(flow.needs, flow.nodes);
  const needs = prepareNeeds(flow.needs);
  const nodes = prepareNodes(flow.nodes);

  flow = createFlow({
    ...flow,
    missingNeeds,
    needs,
    nodes,
    // $FlowFixMe
    taskState: prepareTaskState(flow.taskState),
    extraNeeds: prepareExtraNeeds(needs, nodes),
    givesLoading: {},
    openAddStepPickerNodeId: null,
  });

  return flow;
};

const setFlow = (state: FlowState, flow: MaybeSkinnyFlow): FlowState => {
  return update(['flows', flow.id, $set(flow)], state);
};

const ensureFlowInState = (state: FlowState, flowId): FlowState => {
  if (!state.flows[flowId]) {
    state = update(['flows', flowId, $set(flowById(state, flowId))], state);
  }
  return state;
};

const openFlow = (state, flowId) => {
  if (state.openFlowIds.indexOf(String(flowId)) >= 0) {
    return state;
  }
  return update(['openFlowIds', $end, $set([String(flowId)])], state);
};

const ensureFlowNeedsMeta = (flow, nodeId, fieldKey) => {
  if (!flow.needsMeta[nodeId]) {
    flow = update(['needsMeta', nodeId, $set({})], flow);
  }

  if (!flow.needsMeta[nodeId][fieldKey]) {
    flow = update(['needsMeta', nodeId, fieldKey, $set({ pages: [] })], flow);
  }

  return flow;
};

/**
 * Increments or decrements the nodeId key depending on `incrIfTrue`.
 * The wrapping key is decided by whether we're loading for real or
 * if we're cheating and loading static data.
 */
const setNodeLoading = (flow, nodeId, incrIfTrue, runSource) => {
  const nodeLoadingKey = runSource === 'skip' ? 'nodeSkipping' : 'nodeLoading';
  const apply = incrIfTrue ? old => (old || 0) + 1 : old => (old ? old - 1 : 0);

  flow = update([nodeLoadingKey, nodeId, $apply(apply)], flow);

  return flow;
};

const setNodeUpdateStamp = (flow, nodeId, timestampId) => {
  flow = update(['nodeUpdateStamps', nodeId, $set(timestampId)], flow);

  return flow;
};

const setSampleDirtyFlag = (flow, nodeId, isDirty) => {
  return update(['dirtyTaskState', $merge({ [nodeId]: isDirty })], flow);
};

const updateTaskStates = (flow, nodeId, taskStates, runSource) => {
  if (!flow.isLoaded) {
    return flow;
  }
  if (!_.isEmpty(taskStates)) {
    if (!taskStates.hasCachedSample && wasNodeRan(flow.taskState, nodeId)) {
      // The wasNodeRan is to ensure this isn't a brand new node
      flow = update(['staleSamples', $set({ [nodeId]: true })], flow);
    } else {
      flow = update(['staleSamples', $set({ [nodeId]: false })], flow);
    }

    flow = update(['taskState', $set(prepareTaskState(taskStates))], flow);
  }

  flow = setSampleDirtyFlag(flow, nodeId, false);
  flow = setNodeLoading(flow, nodeId, false, runSource);

  return flow;
};

const updateNodeAttrs = (
  flow,
  nodeId,
  attrs,
  options: { setSampleDirty: boolean } = { setSampleDirty: true }
) => {
  const oldNode = flow.nodes[nodeId];

  const compKeys = ['selected_api', 'action', 'type_of', 'authentication_id'];
  const newAttrs = _.pick(attrs, compKeys);
  const oldAttrs = _.pick(oldNode, _.keys(newAttrs)); // don't compare stuff we didn't save
  const isDirty = !_.isEqual(oldAttrs, newAttrs);

  if (isDirty) {
    // If one of the above keys changed, we need to clear our cache of dynamic dropdown options,
    // but only if those options already exist.
    const newMeta = {};
    _.each(flow.needsMeta[nodeId], (fieldMeta, fieldKey) => {
      newMeta[fieldKey] = { choices: fieldMeta.choices ? [] : null };
    });

    flow = update(['needsMeta', nodeId, $merge(newMeta)], flow);
  }

  flow = update(
    [['nodes', nodeId, $merge(attrs)], ['dirtyNeeds', nodeId, $set(isDirty)]],
    flow
  );

  if (options.setSampleDirty) {
    flow = setSampleDirtyFlag(flow, nodeId, true);
  }

  return flow;
};

const updateNodeValidation = (
  flow: MaybeSkinnyFlow,
  nodeId: ID,
  validation: ?NodePlanLimits
) => {
  const oldNode = flow.nodes[nodeId];
  const oldValidation = _.pick(oldNode, 'plan_limits');
  return !_.isEqual(oldValidation, validation)
    ? update(['nodes', nodeId, 'plan_limits', $set(validation)], flow)
    : flow;
};

const setStateForFlow = (
  state: FlowState,
  flowId: ID,
  updateFlowFn: (flow: MaybeSkinnyFlow) => MaybeSkinnyFlow
): FlowState => {
  return withFlowInState(state, flowId, flow => {
    const updatedFlow = updateFlowFn(flow);
    if (updatedFlow) {
      return setFlow(state, updatedFlow);
    }
    return state;
  });
};
const updateFlowPaused = (flow: MaybeSkinnyFlow, nodeId: ID, paused: boolean) =>
  update(
    [
      ['paused', $set(paused)],
      // does it hold true that if the flow is paused
      // all nodes will be paused also?
      ['nodes', nodeId, 'paused', $set(paused)],
    ],
    flow
  );

// When evicted from LRU, dump fat props, but we keep skinny props around for dashboard.
const unloadFlow = (state: FlowState, flowId: ID): FlowState => {
  return setStateForFlow(state, flowId, flow => {
    if (!flow.isLoaded) {
      return flow;
    }
    const skinnyFlow: any = { ...SKINNY_FLOW_PROPS, id: flow.id };
    Object.keys(flow).forEach(key => {
      if (!_.isObject(flow[key])) {
        skinnyFlow[key] = flow[key];
      }
    });
    Object.keys(flow.nodes).forEach(nodeId => {
      const skinnyNode = {};
      const node = flow.nodes[nodeId];
      Object.keys(node).forEach(key => {
        if (!_.isObject(node[key])) {
          skinnyNode[key] = node[key];
        }
      });
      skinnyFlow.nodes[nodeId] = skinnyNode;
    });
    skinnyFlow.isLoaded = false;
    return skinnyFlow;
  });
};

const removeLruKey = (state: FlowState, key): FlowState => {
  const index = state.lru.indexOf(key);
  if (index >= 0) {
    return update(['lru', $slice(index, index + 1), $set([])], state);
  }
  return state;
};

const addNewLruKey = (state: FlowState, key): FlowState => {
  // Get the key out so it's not duplicated.
  state = removeLruKey(state, key);
  const newLru = state.lru.slice(0);
  // Evict old items from the bottom.
  if (newLru.length >= LRU_MAX) {
    const oldKey = newLru.shift();
    state = unloadFlow(state, oldKey);
  }
  // New items go to the top.
  newLru.push(key);
  return update(['lru', $set(newLru)], state);
};

const updatePrepareNode = (flow: MaybeSkinnyFlow, node: FlowNode): Flow => {
  return update(['nodes', node.id, $set(prepareNode(node))], flow);
};

const setStepMenuState = (state, flowId, index, newValue) => {
  return update(
    ['flows', flowId, 'openStepMenus', index, $set(newValue)],
    state
  );
};

const updateNodeMeta = (
  state: FlowState,
  flowId: ID,
  nodeId: ID,
  meta: NodeMeta
): FlowState => {
  return setStateForFlow(state, flowId, flow => {
    return update(['nodes', nodeId, 'meta', $merge(meta)], flow);
  });
};

const updateIsEditingStepTitle = (
  state: FlowState,
  flowId: ID,
  nodeId: ID,
  isEditing: boolean
) => {
  return setStateForFlow(state, flowId, flow =>
    update(['nodes', nodeId, 'isEditingStepTitle', $set(isEditing)], flow)
  );
};

const updateFlowsRemovalStatus = (state, flowIds, removalStatus) => {
  const updates = flowIds.map(flowId => newState =>
    update(['flows', flowId, 'removalStatus', $set(removalStatus)], newState)
  );

  return _.flow(...updates)(state);
};

/*
 Sets the paused flags on the flow, and the trigger node of the flow.
*/
const setPaused = (state, { flowId, paused }) => {
  if (!isFlowInState(state, flowId)) {
    return state;
  }

  const flow = flowById(state, flowId);
  const newFlow = update(
    [['paused', $set(paused)], ['nodes', flowId, 'paused', $set(paused)]],
    flow
  );

  return update(['flows', flow.id, $set(newFlow)], state);
};

const moveFlowsToTrash = (state, flowIds) => {
  return flowIds.reduce((newState, flowId) => {
    return removeLruKey(
      update(
        [
          'flows',
          flowId,
          ['removalStatus', $set('finished')],
          ['folder_ids', $set([TRASH_FOLDER_ID])],
        ],
        newState
      ),
      flowId
    );
  }, state);
};

const addNodeToFlow = (state, { flowId, node, paused }) => {
  if (!isFlowInState(state, flowId)) {
    return state;
  }

  let flow = flowById(state, flowId);

  // Could be a node already attached to the new node's parent.
  // Do not move path nodes - by definition paths groups can have
  // multiple path node children, and path nodes cannot be moved,
  // they have to be the first node in a path.
  const nodeToMove = _.find(
    _.values(flow.nodes),
    oldNode => oldNode.parent_id === node.parent_id && !isPathNode(oldNode)
  );

  flow = update(
    [['paused', $set(paused)], ['nodes', flowId, 'paused', $set(paused)]],
    flow
  );

  // If so, we will attach it to this node instead.
  if (nodeToMove) {
    const modifiedNodeToMove = _.extend({}, nodeToMove, {
      parent_id: node.id,
    });

    flow = update(['nodes', nodeToMove.id, $set(modifiedNodeToMove)], flow);
  }

  // This is not true to the graph model, but we only do linear graphs (parent => child => child => child) now.
  const parentIndex = flow.nodeIds.indexOf(node.parent_id);
  const nodeIds = flow.nodeIds.slice(0);
  nodeIds.splice(parentIndex + 1, 0, node.id);
  flow = update(['nodeIds', $set(nodeIds)], flow);

  flow = updatePrepareNode(flow, node);

  flow = update(
    [
      ['openAddStepPickerNodeId', $set(null)],
      ['isAddingOrRemovingStep', $set(false)],
    ],
    flow
  );

  return update(['flows', flow.id, $set(flow)], state);
};

const addPathToFlow = (state, { flowId, result }) => {
  const { pathNode, actionNode, needs, paused } = result;

  return _.flow(
    newState =>
      addNodeToFlow(newState, {
        flowId,
        node: pathNode,
        paused,
      }),
    newState =>
      addNodeToFlow(newState, {
        flowId,
        node: actionNode,
        paused,
      }),
    newState =>
      setStateForFlow(newState, flowId, flow =>
        updateNeeds(flow, pathNode.id, needs)
      )
  )(state);
};

const addNewPaths = (state, { flowId, createPathResults, paused }) => {
  // add paths (the nodes, and the needs)
  const updates = createPathResults.map(createPathResult => newState =>
    addPathToFlow(newState, {
      flowId,
      result: createPathResult,
    })
  );
  const newState = _.flow(updates)(state);
  return setPaused(newState, { flowId, paused });
};

const getStepTitle = (flow, nodeId) =>
  _.get(flow, ['nodes', nodeId, 'meta', 'stepTitle']);

const retainStepTitle = (flow, nodeId, stepTitle) =>
  update(['nodes', nodeId, 'meta', $merge({ stepTitle })], flow);

const handlers: HandlersMap = {
  [ActionTypes.ON_WAIT_FOR_SAMPLE_STATE](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          ['sampleStates', 'isPollingForSample', $set(true)],
          ['sampleStates', 'isTimeoutOnSample', $set(false)],
        ],
        flow
      );
    });
  },

  [ActionTypes.ON_LOAD_SAMPLE_STATE](state, { source, flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          ['sampleStates', 'hasWaitedForSample', $set(false)],
          ['sampleStates', 'isWaitingForSample', $set(source !== 'skip')],
        ],
        flow
      );
    });
  },

  [ActionTypes.ON_LOAD_SAMPLE_STATE_DONE](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          ['sampleStates', 'hasWaitedForSample', $set(true)],
          ['sampleStates', 'isWaitingForSample', $set(false)],
        ],
        flow
      );
    });
  },

  [ActionTypes.ON_RETRY_SAMPLE_STATE](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          ['sampleStates', 'isTimeoutOnSample', $set(false)],
          ['sampleStates', 'hasWaitedForSample', $set(false)],
          ['sampleStates', 'isRetryingSample', $set(true)],
        ],
        flow
      );
    });
  },

  [ActionTypes.ON_RETRY_SAMPLE_STATE_DONE](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          ['sampleStates', 'isTimeoutOnSample', $set(false)],
          ['sampleStates', 'hasWaitedForSample', $set(true)],
          ['sampleStates', 'isRetryingSample', $set(false)],
        ],
        flow
      );
    });
  },

  [ActionTypes.ON_COMPLETE_WAIT_FOR_SAMPLE](state, { flowId, isTimeout }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          ['sampleStates', 'isPollingForSample', $set(false)],
          ['sampleStates', 'isTimeoutOnSample', $set(isTimeout)],
        ],
        flow
      );
    });
  },

  [ActionTypes.LOAD_ZAPS](state) {
    // This is an imperfect hack to avoid wiping out a Zap that is created while
    // loading zaps. When we get a store-based router and/or proper queuing,
    // this can be improved.
    return { ...state, isLoading: true };
  },

  [ActionTypes.LOAD_ZAPS_DONE](state, { zaps }) {
    const flows = { ...state.flows };

    const newIds = Object.keys(zaps);
    const oldIds = Object.keys(state.flows);

    const removedIds = _.difference(oldIds, newIds);

    removedIds.forEach(id => {
      if (!flows[id].shouldNotTouchWhenLoadingZaps) {
        delete flows[id];
      }
    });

    const updatedIds = _.intersection(oldIds, newIds);

    updatedIds.forEach(id => {
      // This would be weird if we actually hit this line, but whatevs for now.
      if (!flows[id].shouldNotTouchWhenLoadingZaps) {
        if (
          !flows[id].isLoaded ||
          flows[id].isDirty ||
          hasChangedZap(zaps[id], flows[id])
        ) {
          flows[id] = flows[id].isLoaded
            ? // Overwrite the non-object properties from the skinny 'flow'
              // since they are newer and there was a change
              update(
                [
                  $eachPair,
                  ([key, val]) =>
                    !_.isObject(val) && zaps[id].hasOwnProperty(key),
                  $apply(([key]) => [key, zaps[id][key]]),
                ],
                flows[id]
              )
            : zaps[id];
        }
      }
    });

    const addedIds = _.difference(newIds, oldIds);

    addedIds.forEach(id => {
      flows[id] = zaps[id];
    });

    _.each(flows, flow => {
      if (flow.shouldNotTouchWhenLoadingZaps) {
        const { ...restOfFlow } = flow;
        flows[flow.id] = restOfFlow;
      }
    });

    return update(
      [
        ['isLoading', $set(false)],
        ['isLoaded', $set(true)],
        ['flows', $set(flows)],
      ],
      state
    );
  },

  [ActionTypes.LOAD_FLOW](state, { flowId, isNew }) {
    state = ensureFlowInState(state, flowId);

    state = openFlow(state, flowId);

    state = update(['shouldShowEditorErrors', $set(false)], state);

    return setStateForFlow(state, flowId, flow => {
      flow = update(
        [
          ['isLoaded', $set(false)],
          ['progress', $set(null)],
          ['isNew', $set(isNew)],
        ],
        flow
      );

      // We're in the middle of loading zaps. This might be a new Zap that isn't
      // in that list, so let's protect it.
      if (state.isLoading) {
        flow = update(['shouldNotTouchWhenLoadingZaps', $set(true)], flow);
      }

      return flow;
    });
  },

  [ActionTypes.LOAD_FLOW_DONE](state, { flow }) {
    if (isFlowInState(state, flow.id)) {
      const flowInState = state.flows[flow.id];
      // Retain any loading progress.
      flow = {
        ...flow,
        progress: flowInState.progress,
        isNew: flowInState.isNew,
        publish: flowInState.publish,
        isLoadingAllNeeds: flowInState.isLoadingAllNeeds,
      };
      // If this was loaded while loading zaps, retain that.
      if (flowInState.shouldNotTouchWhenLoadingZaps) {
        flow.shouldNotTouchWhenLoadingZaps = true;
      }
    }

    // Allow all flows for now. Need to limit this, but making them all available for the dashboard.
    state = update(['flows', flow.id, $set(prepareFlow(flow))], state);

    state = addNewLruKey(state, flow.id);

    return state;
  },

  [ActionTypes.LOAD_FLOW_PROGRESS](state, { flowId, progress }) {
    state = ensureFlowInState(state, flowId);

    return setStateForFlow(state, flowId, flow => {
      return update(['progress', $set(progress)], flow);
    });
  },

  [ActionTypes.CLEAR_FLOW_ERRORS_SCROLL](state) {
    return { ...state, didScrollErrors: true };
  },

  [ActionTypes.LOAD_FLOW_PAGE_DONE](state, { flowId, page }) {
    return setStateForFlow(state, flowId, flow => {
      if (!page || !flow.nodes[page.nodeId]) {
        return flow;
      }

      // Make sure page exists.
      if (!flow.isLoaded || !flow.pages[page.nodeId]) {
        flow = update(['pages', page.nodeId, $set({})], flow);
      }

      // Doesn't do anything now, maybe someday.
      flow = update(['pages', page.nodeId, page.pageKey, $set({})], flow);

      return flow;
    });
  },

  [ActionTypes.UPDATE_FLOW_TITLE](state, { flowId, title }) {
    return update(['flows', has(flowId), flowId, 'title', $set(title)], state);
  },

  [ActionTypes.UPDATE_FLOW_TITLE_DONE](state) {
    // TODO: unmark dirty
    return state;
  },

  [ActionTypes.ADD_NODE_DONE](state, props) {
    return addNodeToFlow(state, props);
  },

  [ActionTypes.ADD_NODE_FAIL](state, { flowId }) {
    return update(
      ['flows', flowId, 'isAddingOrRemovingStep', $set(false)],
      state
    );
  },

  [ActionTypes.ADD_STEP](state, { flowId }) {
    return update(
      ['flows', flowId, 'isAddingOrRemovingStep', $set(true)],
      state
    );
  },

  [ActionTypes.ADD_STEP_DONE](state, { flowId }) {
    const newState = update(
      ['flows', flowId, 'isAddingOrRemovingStep', $set(false)],
      state
    );
    return update(
      ['flows', flowId, 'openAddStepPickerNodeId', $set(null)],
      newState
    );
  },

  [ActionTypes.COPY_FLOW_DONE](state, { zap, result }) {
    const flow = update(
      [
        ['nodeIds', $set(_.map(result.nodes, 'id'))],
        ['shouldNotTouchWhenLoadingZaps', $set(true)],
      ],
      result
    );

    const newCopiedZap = getCopiedZap({ state, zap, result });

    state = update(
      [
        ['flows', flow.id, $set(flow)],
        [
          'copiedZaps',
          $set({
            ...state.copiedZaps,
            ...newCopiedZap,
          }),
        ],
        ['lastCopiedZapId', $set(result.id)],
      ],
      state
    );

    state = addNewLruKey(state, result.id);

    return state;
  },

  [ActionTypes.CLEAR_COPIED_ZAPS](state) {
    state = update(
      [['copiedZaps', $set(undefined)], ['lastCopiedZapId', $set(undefined)]],
      state
    );
    return state;
  },

  [ActionTypes.MOVE_FLOW_DONE](state, { flowId, result }) {
    state = update(
      [
        'flows',
        flowId,
        ['folder_ids', $set(result.folder_ids)],
        ['is_shared', $set(result.is_shared)],
      ],
      state
    );
    state = addNewLruKey(state, flowId);

    return state;
  },

  [ActionTypes.REMOVE_FLOW](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['removalStatus', $set('removing')], flow);
    });
  },

  [ActionTypes.REMOVE_FLOW_DONE](state, { flowId }) {
    return moveFlowsToTrash(state, [flowId]);
  },

  [ActionTypes.REMOVE_FLOW_FAIL](state, { flowId }) {
    state = update(['flows', flowId, 'removalStatus', $set('finished')], state);

    return state;
  },

  [ActionTypes.REMOVE_FLOWS](state, { flowIds }) {
    return updateFlowsRemovalStatus(state, flowIds, 'removing');
  },

  [ActionTypes.REMOVE_FLOWS_DONE](state, { flowIds }) {
    return moveFlowsToTrash(state, flowIds);
  },

  [ActionTypes.REMOVE_FLOWS_FAIL](state, { flowIds }) {
    return updateFlowsRemovalStatus(state, flowIds, 'finished');
  },

  [ActionTypes.REMOVE_NODE](state, { flowId, nodeId }) {
    if (!isFlowInState(state, flowId)) {
      return state;
    }

    let flow = flowById(state, flowId);
    const node = flow.nodes[nodeId];

    if (!node) {
      return state;
    }

    flow = update(['isAddingOrRemovingStep', $set(true)], flow);

    // Could be a node attached to this node.
    const childNodeToMove = _.find(
      _.values(flow.nodes),
      childNode => childNode.parent_id === node.id
    );

    // If so, we will attach the child to the parent.
    if (childNodeToMove && !isPathsGroupNode(node) && !isPathNode(node)) {
      const modifiedNodeToMove = _.extend({}, childNodeToMove, {
        parent_id: node.parent_id,
      });

      flow = update(
        [
          ['paused', $set(true)],
          ['nodes', childNodeToMove.id, $set(modifiedNodeToMove)],
        ],
        flow
      );
    }

    const nodeIdsToDelete = getNodeAndDescendants(flow.nodes, nodeId).map(
      n => n.id
    );
    const removals = nodeIdsToDelete.map(id => f => removeNodeFromFlow(f, id));
    const newFlow = _.flow(...removals)(flow);
    return update(['flows', flow.id, $set(newFlow)], state);
  },

  [ActionTypes.REMOVE_NODE_DONE](state, { result }) {
    const { flowId, paused } = result;

    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          ['paused', $set(paused)],
          ['nodes', flowId, 'paused', $set(paused)],
          ['isAddingOrRemovingStep', $set(false)],
        ],
        flow
      );
    });
  },

  [ActionTypes.UPDATE_NODE](
    state,
    { flowId, nodeId, attrs, timestampId, options }
  ) {
    const withUpdatedFlowState = setStateForFlow(state, flowId, flow => {
      const stepTitle = getStepTitle(flow, nodeId);

      // If the user switched to the filter api from a previously selected
      // non-filter app, we need to clear out any previously selected needs,
      // to avoid showing the previous fields when we auto-route to the 'filter'
      // sub-step after selecting the 'filter' app.
      const oldSelectedApi = _.get(flow, ['nodes', nodeId, 'selected_api']);
      if (
        oldSelectedApi &&
        !isFilterAPI(oldSelectedApi) &&
        isFilterAPI(attrs.selected_api)
      ) {
        flow = clearNeeds(flow, nodeId);
      }

      flow = setNodeLoading(flow, nodeId, true);
      flow = setNodeUpdateStamp(flow, nodeId, timestampId);
      flow = updateNodeAttrs(flow, nodeId, attrs, options);
      flow = retainStepTitle(flow, nodeId, stepTitle);

      return flow;
    });
    return update(
      ['shouldShowEditorErrors', $set(false)],
      withUpdatedFlowState
    );
  },

  [ActionTypes.UPDATE_NODE_DONE](state, { flowId, nodeId, node, timestampId }) {
    return setStateForFlow(state, flowId, flow => {
      if (!flow.isLoaded) {
        return flow;
      }
      const updateStamp = flow.nodeUpdateStamps[nodeId];

      // Accept server response as the source of truth for some properties, but
      // only if this is the response from the most recent update.
      if (updateStamp === timestampId) {
        flow = update(
          [
            ['paused', $set(flowId === nodeId ? node.paused : flow.paused)],
            ['nodes', nodeId, 'paused', $set(node.paused)],
            [
              'nodes',
              nodeId,
              'authentication_id',
              $set(node.authentication_id),
            ],
          ],
          flow
        );
      }

      flow = setNodeLoading(flow, nodeId, false);

      return flow;
    });
  },

  [ActionTypes.UPDATE_NODE_FAIL](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow => {
      return setNodeLoading(flow, nodeId, false);
    });
  },

  [ActionTypes.LOAD_NODE_DYNAMIC_DROPDOWN](
    state,
    { flowId, nodeId, fieldKey, timestampId }
  ) {
    if (!isNodeInState(state, flowId, nodeId)) {
      return state;
    }
    let flow = flowById(state, flowId);
    if (!flow.isLoaded || !flow.needs[nodeId]) {
      return state;
    }

    flow = ensureFlowNeedsMeta(flow, nodeId, fieldKey);

    // About to clear the choices, so we have to save the current choice to keep the label.
    const currentValue = flow.nodes[nodeId].params[fieldKey];
    const choices = get(flow.needsMeta, [nodeId, fieldKey, 'choices']);
    const currentChoice = FieldUtils.choiceForValue(currentValue, choices);

    flow = update(
      [
        'needsMeta',
        nodeId,
        fieldKey,
        ['isLoading', $set(true)],
        ['loadStamp', $set(timestampId)],
        ['choices', $set([])],
        ['selectedChoice', $set(currentChoice)],
      ],
      flow
    );

    return update(['flows', flowId, $set(flow)], state);
  },

  // Replaces any choices from the specified page with the new choices for that page.
  // Choices from other pages remain in their page order.
  [ActionTypes.LOAD_NODE_DYNAMIC_DROPDOWN_DONE](
    state,
    { flowId, nodeId, fieldKey, prefillChoices, page, timestampId, prefill }
  ) {
    if (!isNodeInState(state, flowId, nodeId)) {
      return state;
    }
    let flow = flowById(state, flowId);
    if (!flow.isLoaded || !flow.needs[nodeId]) {
      return state;
    }

    const latestLoadStamp = flow.needsMeta[nodeId][fieldKey].loadStamp;

    // Ignore this if there's a more recent load in progress.
    if (latestLoadStamp !== timestampId) {
      return state;
    }

    let pages = [prefillChoices];
    let currentPageCount = pages.length;
    let currentChoiceCount = 0;
    if (page > 0) {
      pages = flow.needsMeta[nodeId][fieldKey].pages.slice(0, page);
      currentChoiceCount = pages.reduce((count, p) => {
        return count + p.length;
      }, 0);
      currentPageCount = pages.length;
      pages[page] = prefillChoices;
    }

    const choices = _.flow(
      _.flatten,
      _.compact,
      function uniqueChoices(flatChoices) {
        const values = new Set();
        return flatChoices.reduce((acc, choice) => {
          const hasChoice = values.has(choice.value);
          values.add(choice.value);
          return hasChoice ? acc : [...acc, choice];
        }, []);
      }
    )(pages);

    if (choices.length === currentChoiceCount) {
      if (pages.length > 1) {
        // If no new choices were added, and this is not the first page,
        // then trim the pages back down, because we don't want to fill
        // pages with dupes.
        pages = pages.slice(0, currentPageCount);
        if (pages[pages.length - 1].length !== 0) {
          // Make sure there's an empty last page as a signal that there are
          // no new pages.
          pages.push([]);
        }
      }
    }

    flow = update(
      [
        'needsMeta',
        nodeId,
        fieldKey,
        ['isLoading', $set(false)],
        ['choices', $set(choices)],
        ['pages', $set(pages)],
        ['nextPage', $set(pages.length)],
        ['prefill', $set(prefill)],
      ],
      flow
    );

    return update(['flows', flowId, $set(flow)], state);
  },

  [ActionTypes.LOAD_NODE_DYNAMIC_DROPDOWN_FAIL](
    state,
    { flowId, nodeId, fieldKey, timestampId }
  ) {
    if (!isNodeInState(state, flowId, nodeId)) {
      return state;
    }
    let flow = flowById(state, flowId);
    if (!flow.isLoaded || !flow.needs[nodeId]) {
      return state;
    }

    const latestLoadStamp = flow.needsMeta[nodeId][fieldKey].loadStamp;

    // Ignore this if there's a more recent load in progress.
    if (latestLoadStamp !== timestampId) {
      return state;
    }

    flow = update(
      [
        'needsMeta',
        nodeId,
        fieldKey,
        ['isLoading', $set(false)],
        ['loadStamp', $set(0)],
      ],
      flow
    );

    return update(['flows', flowId, $set(flow)], state);
  },

  [ActionTypes.REMOVE_NODE_SAMPLE_DONE](state, { flowId, nodeId }) {
    const flow = flowById(state, flowId);
    if (!flow || !flow.isLoaded) {
      return state;
    }
    const newTaskState = getRollbackSample(flow.taskState, String(nodeId));
    return update(['flows', flowId, 'taskState', $set(newTaskState)], state);
  },

  [ActionTypes.ADD_NODE_SUBSCRIPTION](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        ['sampleStates', 'isSubscribing', nodeId, $set(true)],
        flow
      );
    });
  },

  [ActionTypes.ADD_NODE_SUBSCRIPTION_DONE](state, { flowId, nodeId, node }) {
    return setStateForFlow(state, flowId, flow => {
      flow = update(
        ['sampleStates', 'isSubscribing', nodeId, $set(false)],
        flow
      );

      return update(
        [
          'nodes',
          nodeId,
          'subscription_url',
          $set(
            node.subscription_url ||
              'probably 409 but we do not return the object for that'
          ),
        ],
        flow
      );
    });
  },

  [ActionTypes.ADD_NODE_SUBSCRIPTION_FAIL](state, { error, flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow => {
      flow = update(
        ['sampleStates', 'isSubscribing', nodeId, $set(false)],
        flow
      );

      const message = (error.errors || []).join(', ');
      const newTaskState = injectError(message, flow.taskState, String(nodeId));

      return update(['taskState', $set(newTaskState)], flow);
    });
  },

  [ActionTypes.REMOVE_NODE_SUBSCRIPTION](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        ['sampleStates', 'isRemovingSubscription', nodeId, $set(true)],
        flow
      );
    });
  },

  [ActionTypes.REMOVE_NODE_SUBSCRIPTION_DONE](state, { flowId, nodeId, node }) {
    if (!isFlowInState(state, flowId)) {
      return state;
    }

    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          [
            'nodes',
            nodeId,
            'subscription_url',
            $set(node.subscription_url || null),
          ],
          ['sampleStates', 'isRemovingSubscription', nodeId, $set(false)],
        ],
        flow
      );
    });
  },

  [ActionTypes.REMOVE_NODE_SUBSCRIPTION_FAIL](state, { flowId, nodeId, node }) {
    if (!isFlowInState(state, flowId)) {
      return state;
    }

    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          [
            'nodes',
            nodeId,
            'subscription_url',
            $set((node || {}).subscription_url || null),
          ],
          ['sampleStates', 'isRemovingSubscription', nodeId, $set(false)],
        ],
        flow
      );
    });
  },

  [ActionTypes.RUN_NODE](state, { flowId, nodeId, runSource }) {
    return setStateForFlow(state, flowId, flow => {
      return setNodeLoading(flow, nodeId, true, runSource);
    });
  },

  [ActionTypes.RUN_NODE_DONE](
    state,
    { flowId, nodeId, taskStates, runSource }
  ) {
    return setStateForFlow(state, flowId, flow => {
      flow = updateTaskStates(flow, nodeId, taskStates, runSource);
      if (!flow.isLoaded) {
        return flow;
      }
      const isNodeTimeoutOnSample =
        runSource === 'skipped'
          ? false
          : isTimeoutOnSample({ flow: { flows: state.flows } }, { flow });
      return update(
        [
          ['lastTestStatuses', $set(flow.taskState.statuses)],
          ['sampleStates', 'isTimeoutOnSample', $set(isNodeTimeoutOnSample)],
        ],
        flow
      );
    });
  },

  [ActionTypes.RUN_NODE_FAIL](state, { flowId, nodeId, runSource }) {
    return setStateForFlow(state, flowId, flow => {
      flow = update(
        [
          ['sampleStates', 'isWaitingForSample', $set(false)],
          ['lastTestStatuses', nodeId, $set('error')],
        ],
        flow
      );
      return setNodeLoading(flow, nodeId, false, runSource);
    });
  },

  [ActionTypes.REFRESH_NODE](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow => {
      return setNodeLoading(flow, nodeId, true);
    });
  },

  [ActionTypes.REFRESH_NODE_DONE](state, { flowId, nodeId, attrs, result }) {
    const { node, taskStates, needs } = result;

    return setStateForFlow(state, flowId, flow => {
      const stepTitle = getStepTitle(flow, nodeId);

      flow = setNodeLoading(flow, nodeId, false);
      if (node) {
        flow = updateNodeAttrs(flow, nodeId, attrs);
        flow = updateNodeValidation(flow, nodeId, node.validation);
        flow = updatePrepareNode(flow, node);
        flow = updateFlowPaused(flow, nodeId, node.root_paused);
      }
      if (taskStates) {
        flow = updateTaskStates(flow, nodeId, taskStates);
      }
      if (needs) {
        flow = updateNeeds(flow, nodeId, needs);
      }
      if (stepTitle) {
        flow = retainStepTitle(flow, nodeId, stepTitle);
      }
      return flow;
    });
  },

  [ActionTypes.REFRESH_NODE_FAIL](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow => {
      return setNodeLoading(flow, nodeId, false);
    });
  },

  [ActionTypes.LOAD_APP_SAMPLE](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow =>
      update(['appSample', nodeId, $set({ isLoading: true })], flow)
    );
  },

  [ActionTypes.LOAD_APP_SAMPLE_DONE](state, { flowId, nodeId, result }) {
    return setStateForFlow(state, flowId, flow => {
      const appSample = _.get(result, ['state', 'output', nodeId], {});
      return update(['appSample', nodeId, $set(appSample)], flow);
    });
  },

  [ActionTypes.LOAD_APP_SAMPLE_FAIL](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow =>
      update(['appSample', nodeId, $set({})], flow)
    );
  },

  [ActionTypes.LOAD_NODE_NEEDS](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['isLoadingNeeds', $set(true)], flow);
    });
  },

  [ActionTypes.LOAD_NODE_NEEDS_DONE](state, { flowId, nodeId, needs }) {
    if (!isNodeInState(state, flowId, nodeId)) {
      return state;
    }

    // TODO: maybe inject any cached choices/replaceChoices?
    return setStateForFlow(state, flowId, flow => {
      return updateNeeds(flow, nodeId, needs);
    });
  },

  [ActionTypes.LOAD_NODE_NEEDS_FAIL](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['isLoadingNeeds', $set(false)], flow);
    });
  },

  [ActionTypes.LOAD_ALL_NEEDS_FOR_ZAP](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['isLoadingAllNeeds', $set(true)], flow);
    });
  },

  [ActionTypes.LOAD_ALL_NEEDS_FOR_ZAP_DONE](state, { flowId, allNeeds }) {
    return setStateForFlow(state, flowId, flow => {
      const flowWithNeeds = updateAllNeeds(flow, allNeeds);
      return update(['isLoadingAllNeeds', $set(false)], flowWithNeeds);
    });
  },

  [ActionTypes.LOAD_ALL_NEEDS_FOR_ZAP_FAIL](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['isLoadingAllNeeds', $set(false)], flow);
    });
  },

  [ActionTypes.LOAD_NODE_GIVES](state, { flow, nodeId }) {
    if (!isNodeInState(state, flow.id, nodeId)) {
      return state;
    }

    return update(
      ['flows', flow.id, 'givesLoading', nodeId, $set(true)],
      state
    );
  },

  [ActionTypes.LOAD_NODE_GIVES_DONE](state, { flow, nodeId, gives }) {
    if (!isNodeInState(state, flow.id, nodeId)) {
      return state;
    }

    return update(
      [
        ['flows', flow.id, 'givesAll', nodeId, $set(prepareGives(gives))],
        ['flows', flow.id, 'givesLoading', nodeId, $set(false)],
      ],
      state
    );
  },

  [ActionTypes.LOAD_NODE_FAIL](state, { flow, nodeId }) {
    if (!isNodeInState(state, get(flow, 'id'), nodeId)) {
      return state;
    }

    return update(
      ['flows', flow.id, 'givesLoading', nodeId, $set(false)],
      state
    );
  },

  [ActionTypes.LOAD_NODE_INVALID_FIELD_MAPPINGS_DONE](
    state,
    { flowId, nodeId, invalidMappings }
  ) {
    return setStateForFlow(state, flowId, flow => {
      const descendants = getDescendants(flow.nodes, nodeId);
      const updatedDescendants = NodeUtils.mergeNodeMetaInvalidMappings(
        descendants,
        invalidMappings,
        nodeId
      );

      const nodesUpdates = updatedDescendants.map(descendant => [
        descendant.id,
        $set(descendant),
      ]);

      return update(['nodes', nodesUpdates], flow);
    });
  },

  [ActionTypes.UPDATE_NODE_META](state, { flowId, nodeId, meta }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['nodes', nodeId, 'meta', $set(meta)], flow);
    });
  },

  [ActionTypes.UPDATE_NODE_META_DONE](state) {
    return state; // A no-op for now, but wanted to keep with the Done-Fail pattern
  },

  [ActionTypes.UPDATE_NODE_PARAMS](
    state,
    {
      flowId,
      nodeId,
      params,
      fieldKey,
      meta = {},
      isDirtyNeeds = false /*, timestampId = 0 */,
    }
  ) {
    if (!isNodeInState(state, flowId, nodeId)) {
      return state;
    }

    // Make sure arrays and objects have at least one item remaining.
    if (_.isObject(params)) {
      const param = params[fieldKey];
      if (_.isArray(param)) {
        if (param.length === 0) {
          params[fieldKey] = '';
        }
      } else if (_.isObject(param)) {
        if (Object.keys(param).length === 0) {
          params[fieldKey] = { '': '' };
        }
      }
    }

    return setStateForFlow(state, flowId, flow => {
      if (!flow.isLoaded) {
        return flow;
      }

      // If no fieldKey, then just updating default values. So just here to
      // update the params and nothing else.
      if (fieldKey) {
        flow = ensureFlowNeedsMeta(flow, nodeId, fieldKey);
        // Stop forcing re-tests when a user just changes the field mapping
        if (fieldKey !== 'data') {
          flow = setSampleDirtyFlag(flow, nodeId, true);
        }
        flow = update(
          [
            'needsMeta',
            nodeId,
            fieldKey,
            'updateStamp',
            $set(monotonicTimestamp()),
          ],
          flow
        );
      }

      flow = update(
        ['nodes', nodeId, ['params', $merge(params)], ['meta', $merge(meta)]],
        flow
      );

      if (isDirtyNeeds) {
        flow = update(['dirtyNeeds', nodeId, $set(isDirtyNeeds)], flow);
      }

      // TODO: use timestampId to make sure we only use latest
      // if (timestampId) {
      // skip setNodeLoading bool when saving params so the forms don't flicker out
      // flow = setNodeLoading(flow, nodeId, true);
      // }

      return flow;
    });
  },

  [ActionTypes.UPDATE_NODE_PARAMS_DONE](state, { flowId, nodeId, node }) {
    return setStateForFlow(state, flowId, flow => {
      // TODO: use timestampId to make sure we only use latest
      const oldNode = flow.nodes[nodeId];
      if (oldNode) {
        flow = updateFlowPaused(flow, nodeId, node.root_paused);
      }

      // skip setNodeLoading bool when saving params so the forms don't flicker out
      // flow = setNodeLoading(flow, nodeId, false);

      return flow;
    });
  },

  [ActionTypes.SET_FLOW_AUTH_STATUS](state, { flowId, status, error }) {
    return update(
      [
        'flows',
        flowId,
        'testingAuthStatus',
        ['status', $set(status)],
        ['error', $set(error)],
      ],
      state
    );
  },

  [ActionTypes.UNPAUSE_FLOW](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['isToggling', $set(true)], flow);
    });
  },

  [ActionTypes.UNPAUSE_FLOW_DONE](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      const currentEditMode =
        flow.temporaryEditMode ||
        _.get(flow.nodes, [flowId, 'meta', 'edit_mode']);
      // If we're in guided mode, we'll stay there until we leave.
      const temporaryEditMode =
        currentEditMode === GUIDED_EDIT_MODE ? GUIDED_EDIT_MODE : undefined;

      return update(
        [
          ['isToggling', $set(false)],
          ['paused', $set(false)],
          ['state', $set('on')],
          ['temporaryEditMode', $set(temporaryEditMode)],
          [
            'nodes',
            flowId,
            ['meta', 'edit_mode', $set(NORMAL_EDIT_MODE)],
            ['paused', $set(false)],
            ['state', $set('on')],
          ],
        ],
        flow
      );
    });
  },

  [ActionTypes.UNPAUSE_FLOW_FAIL](state, { flowId }) {
    const withUpdatedFlowState = setStateForFlow(state, flowId, flow => {
      return update(['isToggling', $set(false)], flow);
    });
    return update(['dismissed', $set([])], withUpdatedFlowState);
  },

  [ActionTypes.EXIT_FLOW](state, { flowId }) {
    state = setStateForFlow(state, flowId, flow => {
      flow = update(['temporaryEditMode', $none], flow);
      flow = update(['isNew', $none], flow);
      return flow;
    });

    const openFlowIds = _.without(state.openFlowIds, String(flowId));

    return update(['openFlowIds', $set(openFlowIds)], state);
  },

  [ActionTypes.EXIT_FLOWS](state) {
    state.openFlowIds.forEach(flowId => {
      state = handlers[ActionTypes.EXIT_FLOW](state, { flowId });
    });

    return state;
  },

  [ActionTypes.PAUSE_FLOW](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['isToggling', $set(true)], flow);
    });
  },

  [ActionTypes.PAUSE_FLOW_DONE](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          ['isToggling', $set(false)],
          ['paused', $set(true)],
          ['state', $set('off')],
          ['nodes', flowId, ['paused', $set(true)], ['state', $set('off')]],
        ],
        flow
      );
    });
  },

  [ActionTypes.PAUSE_FLOW_FAIL](state, { flowId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['isToggling', $set(false)], flow);
    });
  },

  // Toggle a sidebar step menu open or closed, keyed by step index, zero based
  [ActionTypes.TOGGLE_STEP_MENU](state, { flowId, index, isOpened }) {
    const newValue = _.isUndefined(isOpened)
      ? !state.flows[flowId].openStepMenus[index]
      : isOpened;
    if (state.flows[flowId].openStepMenus[index] === newValue) {
      return state;
    }
    return setStepMenuState(state, flowId, index, newValue);
  },

  // Toggle a sidebar step menu to be open (even if already open)
  [ActionTypes.OPEN_STEP_MENU](state, { flowId, index }) {
    return setStepMenuState(state, flowId, index, true);
  },

  // Toggle a sidebar step menu to be closed (even if already closed)
  [ActionTypes.CLOSE_STEP_MENU](state, { flowId, index }) {
    return setStepMenuState(state, flowId, index, false);
  },

  [ActionTypes.MARK_ZAP_DIRTY](state, { flowId }) {
    return setStateForFlow(state, flowId, flow =>
      update([['isLoaded', $set(false)], ['isDirty', $set(true)]], flow)
    );
  },

  [ActionTypes.CLEAN_NODE_PARAMS](state, { flowId, nodeId }) {
    const oldParams = state.flows[flowId].nodes[nodeId].params;
    const cleanedParams = cleanObjectOfEmptyValues(oldParams);

    return setStateForFlow(state, flowId, flow =>
      update(['nodes', nodeId, 'params', $set(cleanedParams)], flow)
    );
  },

  [ActionTypes.LOAD_RECENT_INCIDENTS_DONE](state, { incidents }) {
    return update(['incidents', $set(incidents)], state);
  },

  // Scrolling silliness, because flow has its own error handling. Yuck.
  [ErrorActionTypes.CLEAR_ERRORS](state) {
    return { ...state, didScrollErrors: true };
  },

  [ActionTypes.EDIT_STEP_TITLE](state, { flowId, nodeId }) {
    return updateIsEditingStepTitle(state, flowId, nodeId, true);
  },

  [ActionTypes.CLOSE_STEP_TITLE_EDITOR](state, { flowId, nodeId, meta }) {
    const stateWithUpdatedNodeMeta = updateNodeMeta(
      state,
      flowId,
      nodeId,
      meta
    );
    return updateIsEditingStepTitle(
      stateWithUpdatedNodeMeta,
      flowId,
      nodeId,
      false
    );
  },

  [ActionTypes.UPDATE_FLOW_NOTES](state, { flowId, note }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['nodes', flowId, 'note', $set(note)], flow);
    });
  },

  [ActionTypes.SAVE_FLOW_NOTES_DONE](state, { flowId, result }) {
    return setStateForFlow(state, flowId, flow => {
      return update(['note', $set(result)], flow);
    });
  },

  [ActionTypes.UPDATE_FLOW_TIMEZONE](state, { flowId, timezone }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          // The BE de-normalizes the timezone and returns it
          // both in the root node meta and in the flow.timezone prop.
          // To avoid showing conflicting results sync both of those here.
          ['nodes', flowId, 'meta', 'timezone', $set(timezone)],
          ['timezone', $set(timezone)],
        ],
        flow
      );
    });
  },

  [ActionTypes.SET_DEFAULT_PREVIEW_TOGGLE_STATE](
    state,
    { flowId, nodeId, isPreviewing }
  ) {
    return setStateForFlow(state, flowId, flow =>
      update(
        ['fieldPageState', nodeId, 'isPreviewing', $set(isPreviewing)],
        flow
      )
    );
  },

  [ActionTypes.TOGGLE_PREVIEW_STATE](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow => {
      return update(
        [
          'fieldPageState',
          nodeId,
          'isPreviewing',
          $apply(isPreviewing => !isPreviewing),
        ],
        flow
      );
    });
  },

  [ActionTypes.GET_PUBLISH_FORM_DONE](state, { flowId, result }) {
    return setStateForFlow(state, flowId, flow =>
      update(['publish', $set(result)], flow)
    );
  },

  [ActionTypes.UPDATE_PUBLISH_FORM](state, { flowId }) {
    return setStateForFlow(state, flowId, flow =>
      update(['publish', 'isWorking', $set(true)], flow)
    );
  },

  [ActionTypes.UPDATE_PUBLISH_FORM_DONE](state, { flowId, result }) {
    return setStateForFlow(state, flowId, flow =>
      update(
        ['publish', ['isWorking', $set(false)], ['value', $merge(result)]],
        flow
      )
    );
  },

  [ActionTypes.UPDATE_PUBLISH_META](state, { flowId, payload }) {
    return setStateForFlow(state, flowId, flow =>
      update(['publish', $merge(payload)], flow)
    );
  },

  [ActionTypes.UPDATE_PUBLISH_MESSAGES](state, { flowId, payload }) {
    return setStateForFlow(state, flowId, flow =>
      update(['publish', 'messages', $merge(payload)], flow)
    );
  },

  [ActionTypes.CREATE_PATH](state, { flowId }) {
    return update(
      ['flows', flowId, 'isAddingOrRemovingStep', $set(true)],
      state
    );
  },

  [ActionTypes.CREATE_PATH_DONE](state, props) {
    return addPathToFlow(state, props);
  },

  [ActionTypes.CREATE_PATH_FAIL](state, { flowId }) {
    return update(
      ['flows', flowId, 'isAddingOrRemovingStep', $set(false)],
      state
    );
  },

  [ActionTypes.CREATE_INITIAL_PATHS_FOR_GROUP_DONE](state, { flowId, result }) {
    const { createPathResults, paused } = result;
    return addNewPaths(state, { flowId, createPathResults, paused });
  },

  [ActionTypes.CREATE_PATHS_GROUP_DONE](state, { flowId, result }) {
    const { pathsGroupNode, createPathResults, paused } = result;

    // add paths group node
    const stateWithPathsGroup = addNodeToFlow(state, {
      flowId,
      node: pathsGroupNode,
      paused,
    });

    // add paths (the nodes, and the needs)
    return addNewPaths(stateWithPathsGroup, {
      flowId,
      createPathResults,
      paused,
    });
  },

  [ActionTypes.CREATE_PATHS_GROUP_FAIL](state, { flowId }) {
    return update(
      ['flows', flowId, 'isAddingOrRemovingStep', $set(false)],
      state
    );
  },

  [ActionTypes.SHOW_EDITOR_ISSUES](state) {
    return update(
      [['shouldShowEditorErrors', $set(true)], ['dismissed', $set([])]],
      state
    );
  },

  [ActionTypes.DISMISS_EDITOR_ERROR](state, { dismissedError }) {
    return update(
      ['dismissed', $set(_.uniq([...(state.dismissed || []), dismissedError]))],
      state
    );
  },

  [ActionTypes.ON_OPEN_ADD_STEP_PICKER](state, { flowId, nodeId }) {
    return update(
      ['flows', flowId, 'openAddStepPickerNodeId', $set(nodeId)],
      state
    );
  },

  [ActionTypes.ON_CLOSE_ADD_STEP_PICKER](state, { flowId, nodeId }) {
    return update(
      ['flows', flowId, 'openAddStepPickerNodeId', $set(nodeId)],
      state
    );
  },

  [ApolloActionTypes.APOLLO_LINK_STATE_SYNC](state, action) {
    return linkStateSync(state, action);
  },

  [ActionTypes.SWITCH_EDITOR](state) {
    return update(['isSwitchingEditors', $set(true)], state);
  },

  [ActionTypes.SWITCH_EDITOR_DONE](state) {
    return update(['isSwitchingEditors', $set(false)], state);
  },

  [ActionTypes.SHOW_SIMPLIFIED_EDITOR_OPT_OUT_SURVEY_MODAL](state) {
    return update(
      ['isSimplifiedEditorOptOutSurveyModalVisible', $set(true)],
      state
    );
  },

  [ActionTypes.HIDE_SIMPLIFIED_EDITOR_OPT_OUT_SURVEY_MODAL](state) {
    return update(
      ['isSimplifiedEditorOptOutSurveyModalVisible', $set(false)],
      state
    );
  },

  [ActionTypes.SAVE_NODE_ATTRS](state, { flowId, nodeId, nodeAttrs }) {
    return setStateForFlow(state, flowId, flow => {
      flow = setNodeLoading(flow, nodeId, true);
      return updateNodeAttrs(flow, nodeId, nodeAttrs);
    });
  },

  [ActionTypes.SAVE_NODE_ATTRS_DONE](state, { flowId, nodeId, node }) {
    return setStateForFlow(state, flowId, flow => {
      flow = setNodeLoading(flow, nodeId, false);
      return updatePrepareNode(flow, node);
    });
  },

  [ActionTypes.SAVE_NODE_ATTRS_FAIL](state, { flowId, nodeId }) {
    return setStateForFlow(state, flowId, flow => {
      return setNodeLoading(flow, nodeId, false);
    });
  },

  [ActionTypes.SCROLL_TO_NODE](state, { flowId, nodeId }) {
    return update(['scrollToNodeIds', flowId, $set(nodeId)], state);
  },

  [ActionTypes.SCROLL_TO_NODE_RESET](state, { flowId }) {
    return update(['scrollToNodeIds', flowId, $none], state);
  },

  [ActionTypes.UPDATE_NODE_VALIDATION](state, { flowId, nodeId, validation }) {
    return setStateForFlow(state, flowId, flow => {
      return updateNodeValidation(flow, nodeId, validation);
    });
  },

  [ActionTypes.CLEAR_NODE_VALIDATION](state, { flowId, nodeId }) {
    // Only clear this specific node if given
    if (flowId && nodeId) {
      const result = setStateForFlow(state, flowId, flow => {
        return updateNodeValidation(flow, nodeId, null);
      });

      return result;
    }

    // Otherwise clear all nodes in the Zap with validation issues
    return setStateForFlow(state, flowId, flow => {
      const hasValidation = has(['plan_limits', v => !_.isNil(v)]);
      return update(
        ['nodes', $each, hasValidation, 'plan_limits', $set(null)],
        flow
      );
    });
  },
};

const reducer = createReducer(handlers, initialState);

type FlowActionType = {
  type: $Keys<typeof handlers>,
  error: any,
  [string]: any,
};

// This silliness must die, but flow has its own errors right now.
const wrappedReducer = (
  state: FlowState,
  action: FlowActionType
): FlowState => {
  let newState = reducer(state, action);

  if (action.error) {
    newState = { ...newState, didScrollErrors: false };
  }

  return newState;
};

export default wrappedReducer;
